import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { enqueueSnackbar } from 'notistack'
import { useTheme } from '@mui/material/styles'
import { Stack, Typography, useMediaQuery, Link, Button } from '@mui/material'
import ReceiptIcon from '@mui/icons-material/Receipt'

import { Types } from 'service-api'
import { captureError } from 'service-api/src/shared'
import { SortOrder } from 'service-api/src/types'

import { HeadCell, Order, SharedTable } from 'table'
import { findLocationUserProfileMembership, membershipCycle } from '@shared/memberships'
import {
  getBenefitOfferLabel,
  getUniqueBenefitOffers,
  getBCRMembershipCycleForDate,
  formatCycleLabel,
  getUniqueBenefits,
  isConsumable,
  getYears,
  getCycles,
} from './helper'
import { buildErrorMessage } from '@shared/error'
import { i18WithParams as t } from '@shared/locales'
import { formatISOStringToMonthShort, getTzDateInUtc } from '@shared/dates'
import { BenefitConsumptionTableFilters } from './benefit_consumption_table_filters'
import { format } from 'date-fns'
import { BenefitConsumption } from 'membership/benefit_consumption'
import { TBenefitConsumptionRecord, TBenefitConsumptionTableFilters, TMembershipCycle } from './types'
import { Today } from '@mui/icons-material'

interface RequestOptions {
  sort?: string
  sortOrder?: SortOrder
  locationUserProfileId?: string
  invoiceStartDate?: string
  limit?: number
  status?: string
  nextToken?: string | null
}

const ROWS_PER_PAGE = [10, 25, 50, 100]
const BENEFIT = 'benefitId'
const MEMBERSHIP = 'membershipId'
const LOCATION = 'location'
const VISIT_DATE = 'invoiceStartDate'
const MEMBERSHIP_CYCLE = 'membershipCycle'
const ACTIONS = 'actions'

const DATE_FILTER_FORMAT = 'yyyy-MM-dd'

export const BenefitConsumptionTable = ({
  locationUserProfile,
  timezone,
  onServiceFetch,
  inlineFilters,
  isSortable,
  isAdmin,
  invoicePath,
  hasSharedCRM,
}: {
  locationUserProfile: Types.LocationUserProfileAdminDto | Types.LocationUserProfileClientDto
  timezone: string
  onServiceFetch?: ({
    locationUserProfileId,
    options,
  }: {
    locationUserProfileId: string
    options: RequestOptions
  }) => Promise<Types.APIBenefitConsumptionRecordAdminDtoResponse | Types.APIBenefitConsumptionRecordClientDtoResponse>
  inlineFilters?: boolean
  isSortable?: boolean
  isAdmin?: boolean
  invoicePath?: (benefitConsumptionRecord: TBenefitConsumptionRecord) => string
  hasSharedCRM?: boolean
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [benefitConsumptionRecords, setBenefitConsumptionRecords] = useState<
    Types.APIBenefitConsumptionRecordAdminDtoResponse | Types.APIBenefitConsumptionRecordClientDtoResponse
  >()
  const [visibleBenefitConsumptionRecords, setVisibleBenefitConsumptionRecords] =
    useState<TBenefitConsumptionRecord[]>()
  const [invoiceFilterDates, setInvoiceFilterDates] = useState<{ startDate: string; endDate: string } | undefined>(
    undefined
  )
  const [benefitsConsumed, setBenefitsConsumed] = useState<TBenefitConsumptionRecord[]>()

  const [allBenefitOffers, setAllBenefitOffers] = useState<string[] | null>(null)
  const [isInitialLoaded, setIsInitialLoaded] = useState(false)
  const [defaultCycle, setDefaultCycle] = useState<TMembershipCycle | undefined>(undefined)
  const [defaultYear, setDefaultYear] = useState<string | undefined>(undefined)

  const [hideConsumptionNumbers, setHideConsumptionNumbers] = useState(false)

  const [isLoading, setIsLoading] = useState(true)
  const [shouldResetPage, setShouldResetPage] = useState(false)
  const [requestOptions, setRequestOptions] = useState<RequestOptions>({
    sort: VISIT_DATE,
    sortOrder: SortOrder.DESC,
    status: 'ACTIVE',
    limit: 50,
  })

  const locationUserProfileMembership = useMemo(
    () => findLocationUserProfileMembership(locationUserProfile?.locationUserProfileMemberships, false) || {},
    [locationUserProfile]
  )
  const isMembershipCancelled = locationUserProfileMembership?.subStatus === 'CANCELED'

  const [years, currentYear, cycles, currentCycle] = useMemo(() => {
    const cycles = getCycles(locationUserProfileMembership, invoiceFilterDates, timezone)
    const currentCycle = membershipCycle(
      locationUserProfileMembership,
      locationUserProfileMembership?.locationMembership?.membership?.billingCycle,
      timezone
    )
    const years = getYears(locationUserProfileMembership)
    const currentYear = currentCycle.startDate.getFullYear().toString()
    return [years, currentYear, cycles, currentCycle]
  }, [locationUserProfileMembership, invoiceFilterDates, timezone])

  const fetchRecords = async (options: RequestOptions = {}, filters?: TBenefitConsumptionTableFilters) => {
    try {
      setShouldResetPage(true)
      const filterOptions = { ...requestOptions, ...options }
      let allResults: TBenefitConsumptionRecord[] = []
      let nextToken: string | null | undefined = undefined
      do {
        const currentOptions: RequestOptions = { ...filterOptions, nextToken }
        const response = await onServiceFetch?.({
          locationUserProfileId: locationUserProfile?.id || '',
          options: currentOptions,
        })
        if (response) {
          allResults = [...allResults, ...response.results]
          nextToken = response.nextToken
        }
      } while (nextToken)

      setBenefitConsumptionRecords({
        results: allResults,
        nextToken: null,
      })

      if (!allBenefitOffers) {
        setAllBenefitOffers(getUniqueBenefitOffers(allResults))
        return
      }

      processRecords(allResults, options, filters)
      setIsInitialLoaded(true)
    } catch (err) {
      enqueueSnackbar(buildErrorMessage(err), { variant: 'error' })
      captureError(err as Error)
    } finally {
      setShouldResetPage(false)
    }
  }

  const fetchEndRecords = async () => {
    const [lastRecord, firstRecord] = await Promise.all([
      onServiceFetch?.({
        locationUserProfileId: locationUserProfile.id || '',
        options: {
          sort: VISIT_DATE,
          sortOrder: SortOrder.DESC,
          limit: 1,
        },
      }),
      onServiceFetch?.({
        locationUserProfileId: locationUserProfile.id || '',
        options: {
          sort: VISIT_DATE,
          sortOrder: SortOrder.ASC,
          limit: 1,
        },
      }),
    ])

    setInvoiceFilterDates({
      startDate: firstRecord?.results[0]?.invoiceStartDate || '',
      endDate: lastRecord?.results[0]?.invoiceStartDate || '',
    })
  }

  const processRecords = (
    records: TBenefitConsumptionRecord[],
    requestOptions?: RequestOptions,
    filterOptions?: { cycle?: TMembershipCycle; benefitOffer?: string }
  ) => {
    let results = [...records].filter((r) => isConsumable(r.benefit!.type))
    if (filterOptions?.cycle) {
      results = results.filter(
        (record) =>
          formatCycleLabel(getBCRMembershipCycleForDate(record, timezone), timezone) ===
          formatCycleLabel(filterOptions.cycle, timezone)
      )
    }

    setBenefitsConsumed(
      sortResults(requestOptions?.sort || VISIT_DATE, requestOptions?.sortOrder || SortOrder.DESC, results)
    )

    if (filterOptions?.benefitOffer && filterOptions.benefitOffer !== t('global.label.all')) {
      results = results.filter((r) => getBenefitOfferLabel(r.benefit!, r.offerId || '') === filterOptions.benefitOffer)
    }

    results = sortResults(requestOptions?.sort || VISIT_DATE, requestOptions?.sortOrder || SortOrder.DESC, results)
    setVisibleBenefitConsumptionRecords(results)
  }

  const benefitConsumptionRecordsList = useMemo(
    () => benefitConsumptionRecords?.results || [],
    [benefitConsumptionRecords]
  )

  const applyFilters = async (filters: TBenefitConsumptionTableFilters) => {
    setIsLoading(true)
    const options: RequestOptions = {}

    if (filters.cycle) {
      options.invoiceStartDate = `gte_${format(filters.cycle.startDate, DATE_FILTER_FORMAT)}_lte_${format(filters.cycle.endDate, DATE_FILTER_FORMAT)}`
      setHideConsumptionNumbers(false)
    } else if (invoiceFilterDates?.startDate && invoiceFilterDates?.endDate) {
      options.invoiceStartDate = `gte_${invoiceFilterDates?.startDate}_lte_${invoiceFilterDates?.endDate}`
      setHideConsumptionNumbers(true)
    }

    await fetchRecords(options, filters)
    setIsLoading(false)
  }

  const clearFilters = async () => {
    const options: RequestOptions = {
      sort: VISIT_DATE,
      sortOrder: SortOrder.DESC,
      invoiceStartDate: undefined,
    }

    if (currentCycle) {
      options.invoiceStartDate = `gte_${format(currentCycle.startDate, DATE_FILTER_FORMAT)}_lte_${format(currentCycle.endDate, DATE_FILTER_FORMAT)}`
      setHideConsumptionNumbers(false)
    }

    await fetchRecords(options)
  }

  const sortResults = (sort: string, sortOrder: Order, results?: TBenefitConsumptionRecord[]) => {
    return (results || benefitConsumptionRecordsList).sort((a, b) => {
      if (sort === BENEFIT) {
        const benefitOfferLabelA = getBenefitOfferLabel(a.benefit!, a.offerId || '')
        const benefitOfferLabelB = getBenefitOfferLabel(b.benefit!, b.offerId || '')
        return sortOrder === SortOrder.DESC
          ? benefitOfferLabelB.localeCompare(benefitOfferLabelA)
          : benefitOfferLabelA.localeCompare(benefitOfferLabelB)
      } else if (sort === VISIT_DATE) {
        return sortOrder === SortOrder.DESC
          ? new Date(b.invoiceStartDate!).getTime() - new Date(a.invoiceStartDate!).getTime()
          : new Date(a.invoiceStartDate!).getTime() - new Date(b.invoiceStartDate!).getTime()
      }
      return 0
    })
  }

  const handleSorting = (sort: string, order: Order) => {
    const options = { ...requestOptions, sort, sortOrder: order as SortOrder }
    setRequestOptions(options)
    processRecords(benefitConsumptionRecordsList, options)
  }

  const currentBenefits = useMemo(
    () => getUniqueBenefits(visibleBenefitConsumptionRecords),
    [visibleBenefitConsumptionRecords]
  )

  const [searchParams] = useSearchParams()
  const targetCycle = searchParams.get('membershipCycle')

  useEffect(() => {
    if (targetCycle) {
      const [startDate, endDate] = targetCycle.split('_')
      const year = startDate.split('-')[0]

      if (!year || !startDate || !endDate) return

      const formattedStartDate = getTzDateInUtc(startDate)
      const formattedEndDate = getTzDateInUtc(endDate)

      const cycle = cycles
        .get(year)
        ?.find(
          (cycle) =>
            formatCycleLabel(cycle) === formatCycleLabel({ startDate: formattedStartDate, endDate: formattedEndDate })
        )

      if (!cycle) return
      setDefaultCycle(cycle)
      setDefaultYear(year)
    }
  }, [targetCycle, cycles])

  useEffect(() => {
    setIsLoading(true)
    Promise.all([fetchRecords(), fetchEndRecords()]).finally(() => {
      setIsLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (defaultCycle) {
      applyFilters({
        benefitOffer: t('global.label.all'),
        year: defaultYear || currentYear,
        cycle: defaultCycle,
      })
    } else {
      applyFilters({
        benefitOffer: t('global.label.all'),
        year: currentYear,
        cycle: currentCycle,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBenefitOffers, currentYear, currentCycle, defaultCycle])

  const headCells = (): HeadCell[] => {
    return [
      {
        id: BENEFIT,
        label: t('global.label.benefit'),
        width: '25%',
        sortable: isSortable,
      },
      ...(isAdmin
        ? [
            {
              id: MEMBERSHIP,
              label: t('global.label.membership'),
              width: '25%',
              sortable: false,
            },
            ...(hasSharedCRM
              ? [
                  {
                    id: LOCATION,
                    label: t('global.label.location'),
                    width: '25%',
                    sortable: false,
                  },
                ]
              : []),
          ]
        : []),
      {
        id: VISIT_DATE,
        label: t('membership.benefit.visitDate'),
        width: '25%',
        sortable: isSortable,
      },
      {
        id: MEMBERSHIP_CYCLE,
        label: t('membership.benefit.membershipCycle'),
        width: '25%',
        sortable: false,
      },
      {
        id: ACTIONS,
        label: t('global.label.actions'),
        align: 'right',
        width: '25%',
        sortable: false,
      },
    ]
  }

  const getRenderRows = (benefitConsumptionRecord: TBenefitConsumptionRecord) => {
    const { benefit, offerId } = benefitConsumptionRecord
    const cycleDates = getBCRMembershipCycleForDate(benefitConsumptionRecord)

    const isInCurrentCycle = formatCycleLabel(cycleDates, timezone) === formatCycleLabel(currentCycle, timezone)

    const rowData: Record<string, React.ReactNode> = {
      [BENEFIT]: (
        <Typography variant="body2bold">
          {getBenefitOfferLabel(benefit!, offerId || '')} {t('membership.benefit.discount.small')}
        </Typography>
      ),
      [VISIT_DATE]: (
        <Typography variant="body2bold">
          {formatISOStringToMonthShort(benefitConsumptionRecord.invoiceStartDate!)}
        </Typography>
      ),
      [MEMBERSHIP_CYCLE]: (
        <Stack direction="row" spacing={1} alignItems="center">
          {isInCurrentCycle && <Today fontSize="small" color="success" />}
          <Typography variant="body2bold">{formatCycleLabel(cycleDates, timezone, 'MMM d, yyyy')}</Typography>
        </Stack>
      ),
      [ACTIONS]: (
        <Stack direction="row" spacing={1} justifyContent="right">
          <Link
            href={invoicePath?.(benefitConsumptionRecord)}
            component={Button}
            sx={{ fontSize: 14, marginTop: '3px', minWidth: 28, textDecoration: 'none' }}
          >
            <ReceiptIcon />
          </Link>
        </Stack>
      ),
    }

    if (isAdmin) {
      rowData[MEMBERSHIP] = (
        <Typography variant="body2bold">
          {benefitConsumptionRecord.locationUserProfileMembership?.membership?.displayName || ''}
        </Typography>
      )
      if (hasSharedCRM) {
        rowData[LOCATION] = (
          <Typography variant="body2bold">
            {benefitConsumptionRecord.location?.displayName || benefitConsumptionRecord.location?.name || ''}
          </Typography>
        )
      }
    }

    return rowData
  }

  return (
    <Stack
      sx={{
        border: '1px solid #E0E0E0',
        borderRadius: '8px',
      }}
    >
      <Stack
        sx={{
          p: 3,
          backgroundColor: theme.palette.info.shade10,
        }}
      >
        <Typography variant="h5">{t('membership.benefit.tableLabel')}</Typography>
        {!inlineFilters && (
          <Stack>
            <BenefitConsumptionTableFilters
              benefitOffers={allBenefitOffers || []}
              years={years}
              cycles={cycles}
              defaultCycle={defaultCycle}
              defaultYear={defaultYear || currentYear}
              currentCycle={currentCycle}
              onApplyFilters={applyFilters}
              onClearFilters={clearFilters}
              hideBenefitFilter={isMembershipCancelled}
            />
          </Stack>
        )}
      </Stack>
      <Stack py={1} px={3}>
        <BenefitConsumption
          hideConsumptionNumbers={hideConsumptionNumbers}
          currentBenefitsUsage={benefitsConsumed}
          currentBenefits={currentBenefits}
          isLoading={!isInitialLoaded || isLoading}
        />
        <SharedTable
          containerStyle={{
            '&&&': {
              padding: 0,
            },
          }}
          component={Stack}
          rows={visibleBenefitConsumptionRecords?.map(getRenderRows) || []}
          head={headCells()}
          defaultOrder="desc"
          defaultOrderBy={VISIT_DATE}
          isLoading={isLoading}
          rowsPerPageConfig={ROWS_PER_PAGE}
          hasMore={false}
          emptyTableMessage={t('global.label.noData')}
          onSort={handleSorting}
          shouldResetPage={shouldResetPage}
          isScrollable={isMobile}
          dropdownFilters={
            inlineFilters ? (
              <BenefitConsumptionTableFilters
                benefitOffers={allBenefitOffers || []}
                years={years}
                cycles={cycles}
                defaultCycle={defaultCycle}
                defaultYear={defaultYear || currentYear}
                currentCycle={currentCycle}
                onApplyFilters={applyFilters}
                onClearFilters={clearFilters}
                hideBenefitFilter={isMembershipCancelled}
                inline={true}
                isLoading={!isInitialLoaded}
              />
            ) : undefined
          }
          dropdownFiltersStyle={inlineFilters ? { backgroundColor: 'transparent', mt: 2, mb: 3 } : undefined}
        />
      </Stack>
    </Stack>
  )
}
